const OPEN_NOTIFICATION = 'OPEN_NOTIFICATION';
const FALLBACK_NOTIFICATION_TAG = 'statusnow-notification';
const SHIPMENT_NOTES_ROUTE = /^\/shipments\/\d+\/notes$/;
const SHIPMENT_DETAILS_ROUTE = /^\/shipments\/[1-9]\d*\/details$/;
const STM_ORDER_ROUTE = /^\/orders\/\d+$/;
const STM_ORDER_HOSTS = new Set([
  'www.asmtrailers.com',
  'test.asmtrailers.com',
  'dev.asmtrailers.com',
]);
const EXPORT_STORAGE_HOSTS = new Set([
  'sastatusnowdev.blob.core.windows.net',
  'sastatusnowprdeast.blob.core.windows.net',
  'sastatusnowtest.blob.core.windows.net',
]);
const DELIVERY_ID = /^[A-Za-z0-9._:-]{1,200}$/;
export const NOTIFICATION_OPEN_CATEGORY_PARAM = 'snNotificationCategory';
export const NOTIFICATION_OPEN_DELIVERY_PARAM = 'snNotificationDelivery';
const MAX_TITLE_LENGTH = 180;
const MAX_BODY_LENGTH = 160;

export const notificationCategories = {
  exports: 'Exports',
  mention: 'Mention',
  shipmentReminder: 'ShipmentReminder',
  smartInboxQuoteGeneration: 'SmartInboxQuoteGeneration',
  stmOrderReceived: 'StmOrderReceived',
  stmEmergencyOrderReceived: 'StmEmergencyOrderReceived',
} as const;

export type NotificationCategory =
  (typeof notificationCategories)[keyof typeof notificationCategories];

export type NotificationPushPayload = {
  body: string;
  category: NotificationCategory;
  deliveryId: string;
  title: string;
  type: 'notification';
  url: string;
  version: 2;
};

export type NotificationData = {
  category?: NotificationCategory;
  deliveryId?: string;
  type: 'notification' | 'unknown';
  url: string;
};

export type NotificationOpen = {
  category: NotificationCategory;
  deliveryId: string;
  url: string;
};

export type ParsedNotificationOpenUrl = {
  cleanUrl: string;
  notification: NotificationOpen | null;
};

export type NotificationPresentation = {
  options: {
    badge: string;
    body: string;
    data: NotificationData;
    icon: string;
    renotify: false;
    tag: string;
  };
  title: string;
};

export type NotificationWindowClient = {
  focus(): Promise<unknown>;
  navigate?(url: string): Promise<NotificationWindowClient | null>;
  postMessage(message: unknown): void;
  url: string;
};

type OpenNotificationWindow = (
  url: string
) => Promise<NotificationWindowClient | null | undefined>;

type OpenNotificationMessage = {
  category?: NotificationCategory;
  deliveryId?: string;
  type: typeof OPEN_NOTIFICATION;
  url: string;
};

export async function networkFirstNavigationResponse(
  network: () => Promise<Response>,
  fallback: () => Promise<Response>
): Promise<Response> {
  let response: Response;
  try {
    response = await network();
  } catch {
    return fallback();
  }

  return response.ok ? response : fallback();
}

export function parseNotificationPushPayload(
  value: unknown,
  origin: string
): NotificationPushPayload | null {
  const payload = asRecord(value);
  const category = parseCategory(payload);
  if (
    !category ||
    !isBoundedString(payload?.['title'], MAX_TITLE_LENGTH) ||
    !isBoundedString(payload['body'], MAX_BODY_LENGTH) ||
    typeof payload['deliveryId'] !== 'string' ||
    !DELIVERY_ID.test(payload['deliveryId'])
  ) {
    return null;
  }

  const url = validateNotificationRoute(payload['url'], origin, category);
  if (!url) {
    return null;
  }

  return {
    body: payload['body'],
    category,
    deliveryId: payload['deliveryId'],
    title: payload['title'],
    type: 'notification',
    url,
    version: 2,
  };
}

export function parseNotificationData(
  value: unknown,
  origin: string
): NotificationData {
  const data = asRecord(value);
  const category =
    parseCategory(data) ??
    parseSupportedCategory(data?.['category']) ??
    (data?.['type'] === 'mention' ? notificationCategories.mention : null);
  const url = category
    ? (validateNotificationRoute(data?.['url'], origin, category) ?? '/')
    : '/';
  const deliveryId =
    typeof data?.['deliveryId'] === 'string' &&
    DELIVERY_ID.test(data['deliveryId'])
      ? data['deliveryId']
      : undefined;

  return {
    category: category ?? undefined,
    deliveryId,
    type: category ? 'notification' : 'unknown',
    url,
  };
}

export function buildNotification(
  payload: NotificationPushPayload | null
): NotificationPresentation {
  if (!payload) {
    return {
      options: {
        badge: '/android-chrome-192x192.png',
        body: 'Open StatusNow to view this notification.',
        data: {
          type: 'unknown',
          url: '/',
        },
        icon: '/android-chrome-192x192.png',
        renotify: false,
        tag: FALLBACK_NOTIFICATION_TAG,
      },
      title: 'Open StatusNow',
    };
  }

  return {
    options: {
      badge: '/android-chrome-192x192.png',
      body: payload.body,
      data: {
        category: payload.category,
        deliveryId: payload.deliveryId,
        type: payload.type,
        url: payload.url,
      },
      icon: '/android-chrome-192x192.png',
      renotify: false,
      tag: payload.deliveryId,
    },
    title: payload.title,
  };
}

export async function openNotification(
  windows: readonly NotificationWindowClient[],
  openWindow: OpenNotificationWindow,
  origin: string,
  data: NotificationData
): Promise<void> {
  const targetUrl = new URL(data.url, origin);
  if (targetUrl.origin !== origin) {
    await openWindow(targetUrl.href);
    return;
  }
  const navigationUrl = notificationNavigationUrl(targetUrl, data);

  const existing = windows.find((client) => {
    try {
      return new URL(client.url).origin === origin;
    } catch {
      return false;
    }
  });

  if (existing) {
    if (existing.navigate) {
      try {
        const navigated = await existing.navigate(navigationUrl.href);
        const target = navigated ?? existing;
        target.postMessage(openNotificationMessage(data));
        await target.focus();
        return;
      } catch {
        // Fall through to Angular navigation for browsers that reject navigate().
      }
    }

    await existing.focus();
    existing.postMessage(openNotificationMessage(data));
    return;
  }

  const opened = await openWindow(navigationUrl.href);
  if (targetUrl.origin === origin) {
    opened?.postMessage(openNotificationMessage(data));
  }
}

export function parseNotificationOpenUrl(
  value: string,
  origin: string
): ParsedNotificationOpenUrl | null {
  try {
    const url = new URL(value, origin);
    if (
      url.origin !== origin ||
      (!url.searchParams.has(NOTIFICATION_OPEN_CATEGORY_PARAM) &&
        !url.searchParams.has(NOTIFICATION_OPEN_DELIVERY_PARAM))
    ) {
      return null;
    }

    const categoryValues = url.searchParams.getAll(
      NOTIFICATION_OPEN_CATEGORY_PARAM
    );
    const deliveryValues = url.searchParams.getAll(
      NOTIFICATION_OPEN_DELIVERY_PARAM
    );
    url.searchParams.delete(NOTIFICATION_OPEN_CATEGORY_PARAM);
    url.searchParams.delete(NOTIFICATION_OPEN_DELIVERY_PARAM);
    const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
    const category =
      categoryValues.length === 1
        ? parseSupportedCategory(categoryValues[0])
        : null;
    const deliveryId = deliveryValues.length === 1 ? deliveryValues[0] : null;
    const route = category
      ? validateNotificationRoute(cleanUrl, origin, category)
      : null;

    return {
      cleanUrl,
      notification:
        category && deliveryId && DELIVERY_ID.test(deliveryId) && route
          ? { category, deliveryId, url: route }
          : null,
    };
  } catch {
    return null;
  }
}

function notificationNavigationUrl(
  targetUrl: URL,
  data: NotificationData
): URL {
  const navigationUrl = new URL(targetUrl.href);
  if (data.category && data.deliveryId) {
    navigationUrl.searchParams.set(
      NOTIFICATION_OPEN_CATEGORY_PARAM,
      data.category
    );
    navigationUrl.searchParams.set(
      NOTIFICATION_OPEN_DELIVERY_PARAM,
      data.deliveryId
    );
  }
  return navigationUrl;
}

function openNotificationMessage(
  data: NotificationData
): OpenNotificationMessage {
  return {
    category: data.category,
    deliveryId: data.deliveryId,
    type: OPEN_NOTIFICATION,
    url: data.url,
  };
}

export function validateNotificationRoute(
  value: unknown,
  origin: string,
  category: NotificationCategory
): string | null {
  if (
    category === notificationCategories.stmOrderReceived ||
    category === notificationCategories.stmEmergencyOrderReceived
  ) {
    return validateStmOrderUrl(value);
  }

  if (category === notificationCategories.exports) {
    return validateExportUrl(value);
  }

  if (
    typeof value !== 'string' ||
    value.length > 300 ||
    !value.startsWith('/') ||
    value.startsWith('//')
  ) {
    return null;
  }

  try {
    const url = new URL(value, origin);
    if (url.origin !== origin || url.hash) {
      return null;
    }

    if (category === notificationCategories.mention) {
      return !url.search && SHIPMENT_NOTES_ROUTE.test(url.pathname)
        ? url.pathname
        : null;
    }

    if (category === notificationCategories.shipmentReminder) {
      return !url.search && SHIPMENT_DETAILS_ROUTE.test(url.pathname)
        ? url.pathname
        : null;
    }

    if (
      category === notificationCategories.smartInboxQuoteGeneration &&
      url.pathname === '/smart-inbox' &&
      [...url.searchParams.keys()].length === 1 &&
      url.searchParams.has('c') &&
      url.searchParams.getAll('c').length === 1
    ) {
      const conversationId = url.searchParams.get('c')?.trim() ?? '';
      return conversationId.length > 0 && conversationId.length <= 200
        ? `${url.pathname}${url.search}`
        : null;
    }

    return null;
  } catch {
    return null;
  }
}

function validateStmOrderUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 300) {
    return null;
  }

  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      STM_ORDER_HOSTS.has(url.hostname) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      STM_ORDER_ROUTE.test(url.pathname)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function validateExportUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048) {
    return null;
  }

  try {
    const url = new URL(value);
    const permissions = url.searchParams.get('sp') ?? '';
    return url.protocol === 'https:' &&
      EXPORT_STORAGE_HOSTS.has(url.hostname) &&
      !url.username &&
      !url.password &&
      !url.hash &&
      url.pathname.startsWith('/data-exports/shipments/') &&
      permissions.includes('r') &&
      !!url.searchParams.get('se') &&
      !!url.searchParams.get('sig')
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function parseCategory(
  payload: Record<string, unknown> | null
): NotificationCategory | null {
  if (payload?.['version'] === 1 && payload['type'] === 'mention') {
    return notificationCategories.mention;
  }

  if (payload?.['version'] !== 2 || payload['type'] !== 'notification') {
    return null;
  }

  return parseSupportedCategory(payload['category']);
}

function parseSupportedCategory(value: unknown): NotificationCategory | null {
  return value === notificationCategories.exports ||
    value === notificationCategories.mention ||
    value === notificationCategories.shipmentReminder ||
    value === notificationCategories.smartInboxQuoteGeneration ||
    value === notificationCategories.stmOrderReceived ||
    value === notificationCategories.stmEmergencyOrderReceived
    ? value
    : null;
}

function isBoundedString(value: unknown, maxLength: number): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maxLength
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}
